<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Device Feature Configuration Templates</h1>
                <v-btn :disabled="!company || !type" color="black"
                       prepend-icon="mdi-plus" @click="createDialogSwitch = true">Add
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="user.type === 'admin'" cols="12" md="4" sm="6">
                <company-select v-model="company" :items="companies" density="compact" label="Select Company"
                          @update:model-value="fetchTemplates"/>
            </v-col>
            <v-col cols="12" md="4" sm="6">
                <v-select v-model="type"
                          :items="templateTypes"
                          density="compact"
                          label="Select Feature" @update:model-value="fetchTemplates"/>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th-btn :sorted-column="sortedColumn??null" column="id"
                                sortable title="ID" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="label"
                                sortable title="Template Name" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="updated_at"
                                sortable title="Last Updated" @sort="sort"/>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="template in templates"
                        v-if="templates.length && !loading" :key="template.id"
                        class="text-left">
                        <td>{{ template.id }}</td>
                        <td>{{ template.label }}</td>
                        <td>{{ template.updated_at ? formatDateTime(template.updated_at) : '' }}</td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editTemplate(template)">Edit
                                </v-btn>
                                <v-btn v-if="(type==='General' && template.type > 1) || type!=='General'"
                                       class="text-error"
                                       prepend-icon="mdi-delete"
                                       size="x-small" @click="deleteTemplate(template)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="7">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchTemplates"
                ></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <Create-Template v-if="createDialogSwitch" v-model="createDialogSwitch" :company-id="company"
                     :type="type.toLowerCase().replace(' ','_')" @addTemplate="addTemplate"/>
    <Edit-Template v-if="editDialogSwitch" v-model="editDialogSwitch" :template="selectedTemplate"
                   :type="type.toLowerCase().replace(' ','_')" @updateTemplate="updateTemplate"/>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import ThBtn from "@/components/globals/th-btn.vue";
import EditTemplate from "@/components/dialogs/monitoring/templates/Edit.vue";
import CreateTemplate from "@/components/dialogs/monitoring/templates/Create.vue";
import CompanySelect from "@/components/globals/company-select.vue";


const {user} = useAuthStore()
const companies = ref([]);
const templates = ref([]);
const loading = ref(false);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref('desc');
const sortedColumn = ref('updated_at');
const selectedTemplate = ref(null);
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const company = ref(user.type !== 'admin' ? user.company.id : null);
const urlPrefix = computed(() => {
    switch (type.value.toLowerCase().replace(' ', '_')) {
        case 'geofence':
            return 'monitoring/geofence';
        case 'light':
            return 'monitoring/light-sensor'
        case 'temperature':
            return 'monitoring/temperature'
        case 'speed':
            return 'monitoring/speed'
        case 'push_button':
            return 'monitoring/push-button'
        case 'io':
            return 'monitoring/ext-gpio'
        case 'ble_tag' :
        case 'wifi_tag':
            return 'monitoring/ble-wifi'
        default:
            return 'monitoring/general'
    }
})
const templateTypes = ref(['Speed', 'Geofence', 'Light', 'Temperature', 'Ble Tag', 'Wifi Tag', 'Push Button', 'IO'])
const type = ref(templateTypes.value[0]);

onMounted(() => {
    if (user.type === 'admin') {
        templateTypes.value.unshift('General');
        fetchCompaniesList();
    } else {
        fetchTemplates();
    }
});
const fetchCompaniesList = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchTemplates();
}
const fetchTemplates = () => {
    if (company.value && type.value) {
        loading.value = true;
        let typeVal = type.value.toLowerCase().replace(' ', '_');
        send('get', `${urlPrefix.value}-templates`, {
            page: currentPage.value,
            company: (user.type === 'admin') ? company.value : null,
            type: typeVal === 'ble_tag' ? 'ble' : (typeVal === 'wifi_tag' ? 'wifi' : null),
            sort: sortedColumn.value,
            order: sortedType.value
        }).then((response) => {
            templates.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        }).catch((error) => {
            console.log(error)
            showSnackbar('Asset Templates could not be loaded', 'error')
        }).finally(() => loading.value = false);
    }
}
const addTemplate = (template) => {
    templates.value.unshift(template);
}
const editTemplate = (template) => {
    selectedTemplate.value = template;
    editDialogSwitch.value = true;
}
const updateTemplate = (template) => {
    templates.value.map((temp, index) => {
        if (temp.id === template.id) {
            templates.value[index] = template;
        }
    });
}
const deleteTemplate = (template) => {
    if (confirm(`Are you sure to delete "${template.label}" template?`)) {
        send('delete', `${urlPrefix.value}-templates/${template.id}`)
            .then((response) => {
                if (response.data.success) {
                    templates.value.map((item, index) => {
                        if (item.id === template.id) {
                            templates.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${template.title}" Template could not be deleted`, 'error')
                }
            })
    }
}
</script>
